import React, {
  Fragment,
  ReactElement,
  useState,
  useCallback,
  useMemo,
  useEffect,
  useRef,
} from 'react';
import { useQueryClient } from 'react-query';
import dayjs from 'dayjs';
import { Transition, Dialog, Disclosure } from '@headlessui/react';
import {
  ArrowSmallDownIcon,
  ArrowSmallUpIcon,
  ArrowTopRightOnSquareIcon,
} from '@heroicons/react/20/solid';
import * as Tooltip from '@radix-ui/react-tooltip';
import { XMarkIcon, ExclamationTriangleIcon } from '@heroicons/react/20/solid';
import * as Popover from '@radix-ui/react-popover';

import {
  Alert,
  Button,
  EmptyState,
  Select,
  Skeleton,
  Spinner,
  TextField,
} from 'src/ui/components';

import {
  useGetCallDocumentPreview,
  useGetQuickExitReasons,
  useGetAlertRevisionsDetails,
} from '../api';

import { classNames } from 'src/utils/className';
import {
  sortDataOption,
  callDocBtnDisableTitle,
  callDocBtnTitle,
} from '../utils/personRepoConstants';
import { usePersonRepoStore } from '../store/personRepoStore';
import { createMarkup } from 'src/utils/common';

import { RelatedCallType } from '../types/relatedCalls';
import { SortDataType } from '../types/personAlertModalType';

export function CallHistoryModal({
  openCallHistoryModal,
  setOpenCallHistoryModal,
  sortData,
  setSortData,
  data,
  tbodyRef,
  isSkeletonLoading,
  isNonBlocking,
  isLoading,
}: {
  openCallHistoryModal: boolean;
  setOpenCallHistoryModal: (val: boolean) => void;
  sortData: SortDataType;
  setSortData: (val: SortDataType) => void;
  data: any;
  tbodyRef: any;
  isSkeletonLoading: boolean;
  isNonBlocking: boolean;
  isLoading: boolean;
}): ReactElement {
  const queryClient = useQueryClient();

  const [callId, setCallId] = useState<number | undefined>(undefined);

  const [selectedCallHistory, setSelectedCallHistory] =
    useState<RelatedCallType | null>(null);

  const selectedPerson = usePersonRepoStore(
    useCallback(state => state.selectedPerson, []),
  );
  const setSelectedPerson = usePersonRepoStore(
    useCallback(state => state.setSelectedPerson, []),
  );
  const callInfos = usePersonRepoStore(
    useCallback(state => state.callInfos, []),
  );
  const deleteCallInfos = usePersonRepoStore(
    useCallback(state => state.deleteCallInfos, []),
  );

  const {
    data: previewContent,
    refetch: onDocumentPreview,
    isLoading: isPreviewLoading,
    isRefetching: isDocPreviewFetching,
  } = useGetCallDocumentPreview(callId);

  const {
    data: revisionDetails,
    isLoading: isRevisionDetailsLoading,
    refetch: refetchAlertRevisions,
  } = useGetAlertRevisionsDetails(
    selectedCallHistory?.AccountId,
    selectedCallHistory?.PersonAlertRevisionId,
  );

  const { data: reasons } = useGetQuickExitReasons();

  //getting the level of care value
  function getLoc(item: number | undefined) {
    if (item === 0) return 'Routine';
    else if (item === 1) return 'Urgent';
    else if (item === 2) return 'Emergent';
    else return '';
  }

  function dataURI(item: any) {
    const value = callInfos?.find((cItem: any) => cItem?.CallId === item?.Id);
    if (value?.Content) {
      return `data:text/html,${encodeURIComponent(value?.Content)}`;
    } else return '';
  }

  const callHistory = useMemo(() => {
    const list = data && data?.pages?.map((page: any) => page.data).flat();
    if (list && list?.length) {
      return list;
    }
    return [];
  }, [data]);

  const callHistoryCsv = useMemo(() => {
    const list = data && data?.pages?.map((page: any) => page.data).flat();
    if (list && list?.length) {
      const csvArray = list?.map((item: any) => {
        return {
          CallId: item?.Id,
          CaseId: item?.CaseId,
          CallDuration:
            item?.Ended !== null
              ? dayjs
                  .utc(dayjs(item?.Ended).diff(dayjs(item?.Started)))
                  .format('HH:mm:ss')
              : '',
          Date: dayjs(item?.Started).format('MM/DD/YYYY H:mm:ss'),
          ServiceType: item?.ServiceType === null ? '' : item?.ServiceType,
          LevelOfCare:
            item?.LevelOfCare === null ? '' : getLoc(item?.LevelOfCare),
        };
      });
      return csvArray;
    }
    return [];
  }, [data]);

  const isNonBlockingModal =
    isNonBlocking ||
    isPreviewLoading ||
    isRevisionDetailsLoading ||
    isDocPreviewFetching;

  function onPrintReport(item: any) {
    const value = callInfos?.find((cItem: any) => cItem?.CallId === item?.Id);
    if (value?.Content) {
      let content = value?.Content;
      const parser = new DOMParser();
      const doc = parser.parseFromString(value?.Content, 'text/html');
      content = doc.documentElement.innerHTML;
      const printWindow = window.open('height=800,width=1090', '_blank');
      printWindow?.document.write(content);
      printWindow?.focus();
      printWindow?.print();
    }
  }

  function formatValue(value: any) {
    if (typeof value === 'string') {
      const escapedValue = value.replace(/"/g, '""');
      return `"${escapedValue}"`;
    }
    return value;
  }

  function formatDate(date: any) {
    const parsedDate = dayjs(date);
    if (parsedDate.isValid()) {
      return `="${dayjs(parsedDate).format('MM/DD/YYYY H:mm:ss')}"`;
    } else {
      console.log(`Invalid date format: ${date}`);
      return date;
    }
  }

  function handleClose() {
    setOpenCallHistoryModal(false);
    deleteCallInfos();
    setSortData({
      sortType: 0,
      sortOrder: 'desc',
      searchValue: '',
    });
    setSelectedPerson({});
  }

  function disableButton(item: RelatedCallType) {
    const value: any = reasons?.find(
      reason => reason.Abbreviation === item?.CallType?.split(',')[0],
    );

    if (item?.Finalized === null) {
      return true;
    }
    if (item?.IsFailed === true) {
      return true;
    }
    if (value === undefined && item?.Finalized !== null) {
      return false;
    }
    if (value?.IsReportRequired === true) {
      return false;
    }
    return true;
  }

  function handleCallDocViewClick(item: any) {
    setCallId(item?.Id);
    queryClient.refetchQueries(['get_call_documents_preview', item?.Id]);
  }

  function DownloadCSV({ data }: { data: any }) {
    function downloadCSVData() {
      const csvRows = [];
      const headers = Object.keys(data[0]);
      csvRows.push(headers.join(','));

      for (const row of data) {
        const formattedRow = headers.map(header => {
          if (header === 'Date') {
            return formatDate(row[header]);
          }
          return formatValue(row[header]);
        });
        csvRows.push(formattedRow.join(','));
      }

      const csvString = csvRows.join('\n');
      const blob = new Blob([csvString], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);

      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'POC-contact-history.csv');

      document.body.appendChild(link);
      link.click();

      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    }
    return (
      <Button size="small" onClick={downloadCSVData}>
        Download CSV
      </Button>
    );
  }

  function renderPersonAlertVersion(item: RelatedCallType) {
    return (
      <div className="flex items-center self-center">
        <Popover.Root>
          {item?.PersonAlertRevisionId &&
          item?.PersonAlertRevisionId !== null ? (
            <Popover.Trigger asChild>
              <Button
                variant="subtle-danger"
                size="small"
                className="!p-1 data-[state=open]:!bg-gray-200 dark:data-[state=open]:bg-neutral-600"
                onClick={e => {
                  setSelectedCallHistory(item);
                  e.stopPropagation();
                  refetchAlertRevisions();
                }}
              >
                <ExclamationTriangleIcon className="h-5 w-5" />
              </Button>
            </Popover.Trigger>
          ) : (
            <div className="size-7" aria-hidden="true" />
          )}
          <Popover.Anchor />
          {!isRevisionDetailsLoading &&
            revisionDetails &&
            revisionDetails.length > 0 && (
              <Popover.Portal>
                <Popover.Content
                  side="left"
                  className="relative max-w-[500px] rounded-xl border border-light-light bg-white shadow-2xl dark:border-dark-medium dark:bg-neutral-800"
                  sideOffset={28}
                >
                  <div className="flex justify-end p-1">
                    <Popover.Close asChild>
                      <button
                        type="button"
                        className="rounded-full p-1 text-gray-500 hover:bg-gray-100 hover:text-gray-800 focus:outline-none dark:hover:bg-neutral-700"
                      >
                        <XMarkIcon className="h-5 w-5" />
                      </button>
                    </Popover.Close>
                  </div>

                  <div className="max-h-[80vh] space-y-4 overflow-y-auto p-4">
                    {revisionDetails.map((revision, index) => (
                      <div
                        key={revision.PersonAlertId ?? index}
                        className="rounded-lg border border-gray-200 p-4 shadow-sm dark:border-neutral-600"
                      >
                        <div className="border-b pb-2 text-lg font-semibold dark:border-neutral-600">
                          Alert: {revision.PersonAlertId}
                        </div>

                        <dl className="space-y-4 pt-2">
                          <div>
                            <dt className="text-sm text-gray-500 dark:text-neutral-300/70">
                              Date range
                            </dt>
                            <dd className="text-sm font-medium">
                              From{' '}
                              {revision.StartDate
                                ? dayjs(revision.StartDate).format(
                                    'MM/DD/YYYY H:mm:ss',
                                  )
                                : ''}{' '}
                              -{' '}
                              {revision.EndDate
                                ? dayjs(revision.EndDate).format(
                                    'MM/DD/YYYY H:mm:ss',
                                  )
                                : ''}
                            </dd>
                          </div>

                          <div>
                            <dt className="text-sm text-gray-500 dark:text-neutral-300/70">
                              Situation description
                            </dt>
                            <dd
                              className="max-h-40 overflow-y-auto rounded-md font-semibold dark:bg-neutral-700"
                              dangerouslySetInnerHTML={createMarkup(
                                revision.SituationDescription,
                              )}
                            ></dd>
                          </div>
                        </dl>

                        <div className="mt-3 flex items-center justify-center rounded-b-lg border-t pt-2 dark:border-neutral-600">
                          <Button
                            variant="link"
                            size="small"
                            onClick={() => {
                              window.open(
                                `/PersonAlert?id=${revision.PersonAlertId}`,
                              );
                            }}
                          >
                            View more details{' '}
                            <ArrowTopRightOnSquareIcon className="size-5" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>

                  <Popover.Arrow className="fill-gray-400 dark:fill-neutral-700" />
                </Popover.Content>
              </Popover.Portal>
            )}
        </Popover.Root>
      </div>
    );
  }

  function renderCallHistoryItem(item: RelatedCallType) {
    return (
      <Disclosure as="div">
        {({ open }) => (
          <>
            <div
              key={item?.Id}
              className={classNames(
                'relative flex rounded-md border border-gray-300 bg-white',
                'dark:border-dark-medium dark:bg-neutral-700 dark:focus-visible:outline-blue-400',
                'focus-visible:outline focus-visible:outline-[3px] focus-visible:outline-offset-[3px] focus-visible:outline-blue-600',
                item.Id === callId ? 'rounded-b-none shadow-none' : 'shadow-sm',
              )}
            >
              <div className="flex grow flex-nowrap gap-2 overflow-x-auto p-2">
                <dl className="flex-1">
                  <dt className="block whitespace-nowrap text-[10px] font-medium uppercase tracking-wide opacity-70">
                    Call ID
                  </dt>
                  <dd className="prevent-text-breakout mt-0.5 text-xs font-semibold tracking-wide text-black  dark:text-white">
                    {item?.Id}
                  </dd>
                </dl>
                <dl className="flex-1">
                  <dt className="block whitespace-nowrap text-[10px] font-medium uppercase tracking-wide opacity-70">
                    Case ID
                  </dt>
                  <dd className="prevent-text-breakout mt-0.5 text-xs font-semibold tracking-wide text-black  dark:text-white">
                    {item?.CaseId === null ? '' : item.CaseId}
                  </dd>
                </dl>
                <dl className="flex-1">
                  <dt className="block whitespace-nowrap text-[10px] font-medium uppercase tracking-wide opacity-70">
                    Call duration
                  </dt>
                  <dd className="prevent-text-breakout mt-0.5 text-xs font-semibold tracking-wide text-black  dark:text-white">
                    {item?.Ended !== null
                      ? dayjs
                          .utc(dayjs(item?.Ended).diff(dayjs(item?.Started)))
                          .format('HH:mm:ss')
                      : ''}
                  </dd>
                </dl>
                <dl className="flex-1">
                  <dt className="block whitespace-nowrap text-[10px] font-medium uppercase tracking-wide opacity-70">
                    Date
                  </dt>
                  <dd className="prevent-text-breakout mt-0.5 text-xs font-semibold tracking-wide text-black  dark:text-white">
                    {dayjs(item?.Started).format('MM/DD/YYYY H:mm:ss')}
                  </dd>
                </dl>
                <dl className="flex-1">
                  <dt className="block whitespace-nowrap text-[10px] font-medium uppercase tracking-wide opacity-70">
                    Service type
                  </dt>
                  <dd className="prevent-text-breakout mt-0.5 text-xs font-semibold tracking-wide text-black  dark:text-white">
                    {item?.ServiceType === null ? '' : item?.ServiceType}
                  </dd>
                </dl>
                <dl className="flex-1">
                  <dt className="block whitespace-nowrap text-[10px] font-medium uppercase tracking-wide opacity-70">
                    Level of care
                  </dt>
                  <dd className="prevent-text-breakout mt-0.5 text-xs font-semibold tracking-wide text-black  dark:text-white">
                    {item?.LevelOfCare === null
                      ? ''
                      : getLoc(item?.LevelOfCare)}
                  </dd>
                </dl>
                {renderPersonAlertVersion(item)}
              </div>
              <div className="flex flex-col items-center justify-around gap-0.5 border-l border-gray-300 p-2 dark:border-neutral-600 sm:flex-row">
                <Tooltip.Provider>
                  <Tooltip.Root>
                    <Tooltip.Trigger asChild>
                      <div className="space-y-1">
                        <Disclosure.Button
                          as={Button}
                          variant="subtle"
                          onClick={() => {
                            handleCallDocViewClick(item);
                          }}
                          size="small"
                          disabled={disableButton(item)}
                        >
                          <svg
                            className="size-[15px]"
                            viewBox="0 0 15 15"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                          >
                            <path
                              d="M4.2 1H4.17741H4.1774C3.86936 0.999988 3.60368 0.999978 3.38609 1.02067C3.15576 1.04257 2.92825 1.09113 2.71625 1.22104C2.51442 1.34472 2.34473 1.51442 2.22104 1.71625C2.09113 1.92825 2.04257 2.15576 2.02067 2.38609C1.99998 2.60367 1.99999 2.86935 2 3.17738V3.1774V3.2V11.8V11.8226V11.8226C1.99999 12.1307 1.99998 12.3963 2.02067 12.6139C2.04257 12.8442 2.09113 13.0717 2.22104 13.2837C2.34473 13.4856 2.51442 13.6553 2.71625 13.779C2.92825 13.9089 3.15576 13.9574 3.38609 13.9793C3.60368 14 3.86937 14 4.17741 14H4.2H10.8H10.8226C11.1306 14 11.3963 14 11.6139 13.9793C11.8442 13.9574 12.0717 13.9089 12.2837 13.779C12.4856 13.6553 12.6553 13.4856 12.779 13.2837C12.9089 13.0717 12.9574 12.8442 12.9793 12.6139C13 12.3963 13 12.1306 13 11.8226V11.8V3.2V3.17741C13 2.86936 13 2.60368 12.9793 2.38609C12.9574 2.15576 12.9089 1.92825 12.779 1.71625C12.6553 1.51442 12.4856 1.34472 12.2837 1.22104C12.0717 1.09113 11.8442 1.04257 11.6139 1.02067C11.3963 0.999978 11.1306 0.999988 10.8226 1H10.8H4.2ZM3.23875 2.07368C3.26722 2.05623 3.32362 2.03112 3.48075 2.01618C3.64532 2.00053 3.86298 2 4.2 2H10.8C11.137 2 11.3547 2.00053 11.5193 2.01618C11.6764 2.03112 11.7328 2.05623 11.7613 2.07368C11.8285 2.11491 11.8851 2.17147 11.9263 2.23875C11.9438 2.26722 11.9689 2.32362 11.9838 2.48075C11.9995 2.64532 12 2.86298 12 3.2V11.8C12 12.137 11.9995 12.3547 11.9838 12.5193C11.9689 12.6764 11.9438 12.7328 11.9263 12.7613C11.8851 12.8285 11.8285 12.8851 11.7613 12.9263C11.7328 12.9438 11.6764 12.9689 11.5193 12.9838C11.3547 12.9995 11.137 13 10.8 13H4.2C3.86298 13 3.64532 12.9995 3.48075 12.9838C3.32362 12.9689 3.26722 12.9438 3.23875 12.9263C3.17147 12.8851 3.11491 12.8285 3.07368 12.7613C3.05624 12.7328 3.03112 12.6764 3.01618 12.5193C3.00053 12.3547 3 12.137 3 11.8V3.2C3 2.86298 3.00053 2.64532 3.01618 2.48075C3.03112 2.32362 3.05624 2.26722 3.07368 2.23875C3.11491 2.17147 3.17147 2.11491 3.23875 2.07368ZM5 10C4.72386 10 4.5 10.2239 4.5 10.5C4.5 10.7761 4.72386 11 5 11H8C8.27614 11 8.5 10.7761 8.5 10.5C8.5 10.2239 8.27614 10 8 10H5ZM4.5 7.5C4.5 7.22386 4.72386 7 5 7H10C10.2761 7 10.5 7.22386 10.5 7.5C10.5 7.77614 10.2761 8 10 8H5C4.72386 8 4.5 7.77614 4.5 7.5ZM5 4C4.72386 4 4.5 4.22386 4.5 4.5C4.5 4.77614 4.72386 5 5 5H10C10.2761 5 10.5 4.77614 10.5 4.5C10.5 4.22386 10.2761 4 10 4H5Z"
                              fill="currentColor"
                              fillRule="evenodd"
                              clipRule="evenodd"
                            ></path>
                          </svg>
                        </Disclosure.Button>
                      </div>
                    </Tooltip.Trigger>
                    <Tooltip.Portal>
                      <Tooltip.Content
                        className="max-w-xs select-none rounded bg-white px-3 py-2 text-sm shadow-xl dark:bg-neutral-700"
                        sideOffset={5}
                      >
                        {disableButton(item) === true
                          ? callDocBtnDisableTitle
                          : callDocBtnTitle}
                        <Tooltip.Arrow className="fill-white dark:fill-neutral-700" />
                      </Tooltip.Content>
                    </Tooltip.Portal>
                  </Tooltip.Root>
                </Tooltip.Provider>
              </div>
            </div>
            {renderReportItem(item)}
          </>
        )}
      </Disclosure>
    );
  }

  function renderReportItem(item: RelatedCallType) {
    return (
      <Transition
        enter="transition ease-out duration-200"
        enterFrom="opacity-0 -translate-y-1"
        enterTo="opacity-100 translate-y-0"
        leave="transition ease-in duration-150"
        leaveFrom="opacity-100 translate-y-0"
        leaveTo="opacity-0 -translate-y-1"
      >
        {item.Id === callId && (
          <div
            className={classNames(
              'min-h-56 overflow-hidden rounded-b-md border border-t-0 border-gray-300 bg-white dark:border-dark-medium dark:bg-neutral-700',
            )}
            key={item?.Id}
          >
            {dataURI(item) === '' &&
            !isPreviewLoading &&
            !isDocPreviewFetching ? (
              <div className="absolute inset-0 grid  place-content-center">
                <EmptyState message="No information available" />
              </div>
            ) : (
              !isPreviewLoading && (
                <div className="p-3">
                  <iframe
                    title="Dynamic HTML Content"
                    src={dataURI(item)}
                    width="100%"
                    height="100%"
                    className="h-[calc(100vh-380px)]"
                  />
                </div>
              )
            )}

            {dataURI(item) && dataURI(item) !== '' && (
              <div className="flex justify-end gap-2 border-t border-gray-300 bg-gray-50 p-2 dark:border-dark-medium dark:bg-neutral-800">
                <Button
                  variant="secondary"
                  size="small"
                  onClick={() => onPrintReport(item)}
                >
                  Print
                </Button>
              </div>
            )}
          </div>
        )}
      </Transition>
    );
  }

  return (
    <Transition appear show={openCallHistoryModal} as={Fragment}>
      <Dialog as="div" className="relative" onClose={() => handleClose()}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="pr-dialog__backdrop" />
        </Transition.Child>

        <div className="pr-dialog__wrapper">
          <div className="pr-dialog__panel__container">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className="pr-dialog__panel flex h-[calc(100svh-110px)] max-h-[calc(100svh-70px)] flex-col sm:!max-w-5xl">
                <div className="flex items-center gap-3 border-b p-4 dark:border-dark-medium">
                  <Dialog.Title
                    as="h3"
                    className="grow truncate text-lg font-medium leading-6 text-gray-900 dark:text-white"
                  >
                    POC Contact history
                  </Dialog.Title>

                  <button
                    type="button"
                    className=" text-gray-400 hover:text-gray-500 sm:right-4 sm:top-4"
                    onClick={e => {
                      e.stopPropagation();
                      handleClose();
                    }}
                  >
                    <span className="sr-only">Close</span>
                    <XMarkIcon className="h-6 w-6" aria-hidden="true" />
                  </button>
                </div>
                <div className="flex flex-wrap items-end justify-between gap-4 p-4 pt-2">
                  <div className="flex shrink-0 items-end gap-2">
                    <TextField
                      inputSize="small"
                      type="search"
                      id="search-filed"
                      value={sortData?.searchValue}
                      onChange={(e: any) => {
                        setSortData({
                          ...sortData,
                          searchValue: e.target.value,
                        });
                      }}
                      label="Search"
                    />

                    <Select
                      label="Sort"
                      id="id-sort-person-repo"
                      options={sortDataOption}
                      onChange={e => {
                        setSortData({
                          ...sortData,
                          sortType: parseInt(e.target.value),
                        });
                      }}
                      value={sortData.sortType}
                      inputSize="small"
                    />

                    <Button
                      variant={
                        sortData.sortOrder === 'asc' ? 'primary' : 'secondary'
                      }
                      size="small"
                      onClick={() =>
                        setSortData({ ...sortData, sortOrder: 'asc' })
                      }
                    >
                      <ArrowSmallUpIcon
                        className="h-5 w-5"
                        aria-hidden="true"
                      />
                      <span className="sr-only">Sort in ascending order</span>
                    </Button>
                    <Button
                      variant={
                        sortData.sortOrder === 'desc' ? 'primary' : 'secondary'
                      }
                      size="small"
                      onClick={() =>
                        setSortData({ ...sortData, sortOrder: 'desc' })
                      }
                    >
                      <ArrowSmallDownIcon
                        className="h-5 w-5"
                        aria-hidden="true"
                      />
                      <span className="sr-only">Sort in descending order</span>
                    </Button>
                  </div>
                  {callHistory?.length > 0 && (
                    <DownloadCSV data={callHistoryCsv} />
                  )}
                </div>
                <div
                  className="relative  min-h-0 grow space-y-3 overflow-auto overflow-y-scroll scroll-smooth bg-gray-100 p-4 dark:bg-neutral-800"
                  ref={tbodyRef}
                >
                  <Alert variant="warning" size="small" className="lg:hidden">
                    Call Documents will be unavailable for calls that are not
                    finalized
                  </Alert>
                  {!isLoading && data?.pages ? (
                    <>
                      {data?.pages.map((page: any, index: number) => (
                        <Fragment key={`${page} - ${index + 1}`}>
                          {page?.data && page?.data?.length ? (
                            page?.data.map((personInfo: any, indx: number) =>
                              renderCallHistoryItem(personInfo),
                            )
                          ) : (
                            <div className="absolute inset-0 grid place-content-center ">
                              <EmptyState message="No information available" />
                            </div>
                          )}
                        </Fragment>
                      ))}
                    </>
                  ) : (
                    isSkeletonLoading && <Skeleton count={8} />
                  )}
                  {isNonBlockingModal && (
                    <div className="fixed inset-0 z-20 grid place-content-center">
                      <Spinner size="large" />
                    </div>
                  )}
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
}
